import * as Sentry from '@sentry/browser';
import { ActionKeys } from '~/actions/constants';
import * as gtmActions from '~/actions/gtm.actions';
import * as packagesActions from '~/actions/packages.actions';
import { fetchParcelTemplates } from '~/actions/parcel-template.actions';
import { sendTrackEvent } from '~/actions/prod-analytics.actions';
import { closeModal } from '~/actions/settings-packages.actions';
import { SET_FIRST_PACKAGE_TEMPLATE } from '~/constants/gtm';
import { PACKAGE_TEMPLATE_MODAL } from '~/pages/Settings/Packages/SavePackageTemplateModal/SavePackageTemplateModal';
import {
  getFormValues,
  reset,
  setSubmitFailed,
  setSubmitSucceeded,
  startSubmit,
  stopSubmit,
} from 'redux-form';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { getIsOldExpChosen } from '~/selectors/features.selectors';
import { getParcelTemplates } from '~/selectors/parcel-template.selectors';
import * as packagesService from '~/services/packages.service';
import ParcelTemplate, { fromJSON, JSONParcelTemplate, toJSON } from '~/types/ParcelTemplate';

// Redux Form
import { applyParcelTemplateToOrderParcelForm, updateOrderParcel } from '~/actions/orders.actions';
import axios, { AxiosResponse } from 'axios';
import { PARCEL_FORM_NAME } from '~/constants/form';
import { DEFAULT_PACKAGE_UNITS_FORM } from '~/pages/Settings/Packages/DefaultPackageUnitsForm';
import { OrdersActions } from '~/reducers/orders/orders.reducer';
import { getOrderAction, getSelectedOrders } from '~/selectors/orders.selectors';
import { fromParcelTemplate } from '~/types/Parcel';
import { Events, track } from '~/utils/segment';
import { selectState } from '..';
import { requestUpdatedTrack } from '~/utils/TrackingHelper/utils';

export function* fetchDefaultPackageUnits() {
  try {
    const response: AxiosResponse<{ weight_unit: string; dimension_unit: string }> = yield call(
      packagesService.get,
    );
    const payload: packagesActions.DefaultPackageUnitsPayload = {
      defaultWeightUnit: response.data.weight_unit,
      defaultDistanceUnit: response.data.dimension_unit,
    };
    yield put(packagesActions.fetchDefaultPackageUnitsSuccess(payload));
  } catch (err) {
    yield put(packagesActions.fetchDefaultPackageUnitsError(err.message));
  }
}

export function* updateDefaultPackageUnits(
  action: ReturnType<typeof packagesActions.updateDefaultPackageUnits>,
) {
  try {
    const servicePayload = {
      weight_unit: action.payload.defaultWeightUnit,
      dimension_unit: action.payload.defaultDistanceUnit,
    };
    yield put(startSubmit(DEFAULT_PACKAGE_UNITS_FORM));
    yield call(packagesService.put, servicePayload);
    yield put(packagesActions.updateDefaultPackageUnitsSuccess(action.payload));
    yield call(track, Events.save_default_package_units);
    yield put(stopSubmit(DEFAULT_PACKAGE_UNITS_FORM));
    yield put(setSubmitSucceeded(DEFAULT_PACKAGE_UNITS_FORM));
  } catch (err) {
    yield put(packagesActions.fetchDefaultPackageUnitsError(err.message));
    yield put(stopSubmit(DEFAULT_PACKAGE_UNITS_FORM));
    yield put(setSubmitFailed(DEFAULT_PACKAGE_UNITS_FORM));
  }
}

export function* savePackageTemplate(
  action: ReturnType<typeof packagesActions.savePackageTemplate>,
) {
  try {
    yield put(startSubmit(PACKAGE_TEMPLATE_MODAL));
    const isOldExpChosen: boolean = yield selectState<boolean>(getIsOldExpChosen);

    const useNewExperienceEndpoints = CONFIG.flags.useNewParcelTemplateEndpoints && !isOldExpChosen;
    const parcelTemplate = action.payload.formData;
    const applyNewParcelTemplateOnParcelForm = action.payload.applyNewParcelTemplateOnParcelForm;
    let response: AxiosResponse<JSONParcelTemplate>;
    if (parcelTemplate.objectId) {
      response = useNewExperienceEndpoints
        ? yield call(
            [packagesService, packagesService.update],
            toJSON(action.payload.formData),
            parcelTemplate.objectId,
          )
        : yield call(
            [packagesService, packagesService.updateOldExperience],
            toJSON(action.payload.formData),
            parcelTemplate.objectId,
          );
    } else {
      response = useNewExperienceEndpoints
        ? yield call([packagesService, packagesService.create], toJSON(action.payload.formData))
        : yield call(
            [packagesService, packagesService.createOldExperience],
            toJSON(action.payload.formData),
          );
    }

    requestUpdatedTrack(action.payload.trackingReference, {
      url: response.config.url || '',
      isError: false,
      status: response.status,
      message: undefined,
    });

    yield put(fetchParcelTemplates());
    yield put(closeModal());
    yield put(stopSubmit(PACKAGE_TEMPLATE_MODAL));
    yield put(reset(PACKAGE_TEMPLATE_MODAL));

    const newParcelTemplate = fromJSON(response.data);

    yield put(
      packagesActions.savePackageTemplateSuccess(
        newParcelTemplate!,
        action.payload.trackingReference,
        applyNewParcelTemplateOnParcelForm,
      ),
    );

    const parcelTemplates: readonly ParcelTemplate[] =
      yield selectState<readonly ParcelTemplate[]>(getParcelTemplates);

    // Only triggers when there are no current parcel templates in the redux store
    if (parcelTemplates.length === 0) {
      // Calling GTM Trigger to track when a package template is saved
      yield put(
        gtmActions.sendGTMEvent({
          eventName: SET_FIRST_PACKAGE_TEMPLATE,
        }),
      );

      // Calling track event action for Heap
      yield put(sendTrackEvent('Set First Package Template'));
    }
  } catch (err) {
    if (axios.isAxiosError(err)) {
      requestUpdatedTrack(action.payload.trackingReference, {
        url: err.config.url || '',
        isError: true,
        status: err.response?.status || -1,
        message: err.message,
      });
    }
    yield put(stopSubmit(PACKAGE_TEMPLATE_MODAL));
  }
}

export function* savePackageTemplateSuccess(
  action: ReturnType<typeof packagesActions.savePackageTemplateSuccess>,
) {
  try {
    const { applyNewParcelTemplateOnParcelForm, parcelTemplate } = action.payload;
    if (applyNewParcelTemplateOnParcelForm) {
      // Fetch the "useImportedOrderWeight" property from the form pre-update
      const formValues: { useImportedOrderWeight: boolean } | undefined = yield select(
        getFormValues(PARCEL_FORM_NAME),
      );

      // Apply new parcel template to the order parcel form so the UI is updated
      yield put(applyParcelTemplateToOrderParcelForm(parcelTemplate));

      const parcel = fromParcelTemplate(parcelTemplate);
      const orderAction: OrdersActions = yield selectState<OrdersActions>(getOrderAction);
      let workingOrders = {};

      // Apply parcel template onto order's parcel if weight is set
      if (!!parcel.weight && parcel.weight !== '0' && !formValues?.useImportedOrderWeight) {
        // single order edit
        if (orderAction.kind === 'order-edit') {
          workingOrders = orderAction.orderSelection;
        }

        // if it's not there, get it from the selectedOrders
        if (Object.keys(workingOrders).length < 1) {
          workingOrders = yield select(getSelectedOrders);
        }

        if (workingOrders) {
          yield put(updateOrderParcel(workingOrders, parcel, action.payload.trackingReference));
        }
      }
    }
  } catch (err) {
    Sentry.captureException(err);
  }
}

export function* deletePackageTemplate(
  action: ReturnType<typeof packagesActions.deletePackageTemplate>,
) {
  try {
    const isOldExpChosen: boolean = yield selectState<boolean>(getIsOldExpChosen);
    const useNewExperienceEndpoints = CONFIG.flags.useNewParcelTemplateEndpoints && !isOldExpChosen;
    let response;

    if (useNewExperienceEndpoints) {
      response = yield call([packagesService, packagesService.del], action.payload.objectId);
    } else {
      response = yield call(
        [packagesService, packagesService.delOldExperience],
        action.payload.objectId,
      );
    }

    requestUpdatedTrack(action.payload.trackingReference, {
      url: response.config.url || '',
      isError: false,
      status: response.status,
      message: undefined,
    });

    yield put(packagesActions.deletePackageTemplateSuccess(action.payload.objectId));
    yield put(closeModal());
  } catch (err) {
    if (axios.isAxiosError(err)) {
      requestUpdatedTrack(action.payload.trackingReference, {
        url: err.config.url || '',
        isError: true,
        status: err.response?.status || -1,
        message: err.message,
      });
    }
    yield put(packagesActions.deletePackageTemplateError());
  }
}

export default [
  takeLatest(ActionKeys.FETCH_PACKAGE_DEFAULT_UNITS, fetchDefaultPackageUnits),
  takeLatest(ActionKeys.UPDATE_PACKAGE_DEFAULT_UNITS, updateDefaultPackageUnits),
  takeLatest(ActionKeys.SAVE_PACKAGE_TEMPLATE, savePackageTemplate),
  takeLatest(ActionKeys.DELETE_PACKAGE_TEMPLATE, deletePackageTemplate),
  takeLatest(ActionKeys.SAVE_PACKAGE_TEMPLATE_SUCCESS, savePackageTemplateSuccess),
];
